import SendIcon from "@mui/icons-material/Send";
import type { SelectChangeEvent } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { IconButton } from "@citrine/atoms/icon-button";
import { Select } from "@citrine/atoms/select";
import { TextField } from "@citrine/atoms/text-field";
import { TruncatedText } from "@citrine/atoms/truncated-text";
import { AgentsClient } from "@citrine/resources/agents";
import { useConfiguration } from "@citrine/resources/configuration";

import { useAgentStream } from "#src/components/catalyst/hooks/use-agent-stream";
import { useCatalystContext } from "#src/contexts/catalyst";

import { AgentChatMessageList } from "./agent-chat-message-list";
import * as sx from "./agent-chat-view.sx";
import { CatalystHeader } from "./catalyst-header";
import * as catalystSx from "./catalyst.sx";
import { useAgentRoute } from "./hooks/use-agent-route";
import { TEST_IDS } from "./utils";

/**
 * Resolve the dropdown agent: explicit selection wins, else the route default
 * if it's among the fetched options, else the first option (falling back to the
 * route default so an empty list still renders something).
 */
function resolveAgentKey(
  explicit: string | undefined,
  routeDefault: string,
  options: readonly { value: string }[],
): string {
  if (explicit) {
    return explicit;
  }
  if (options.some((option) => option.value === routeDefault)) {
    return routeDefault;
  }
  return options[0]?.value ?? routeDefault;
}

export function AgentChatView() {
  const { enableCatalystAgentDebug } = useConfiguration();
  const { formatMessage } = useIntl();

  const { defaultAgent, context: routeContext } = useAgentRoute();
  const { expanded, optimizationRunId } = useCatalystContext();

  const context = useMemo(
    () =>
      optimizationRunId ? { ...routeContext, optimizationRunId } : routeContext,
    [routeContext, optimizationRunId],
  );

  const { data: agents } = AgentsClient.getInstance().useList();
  const agentOptions = useMemo(
    () =>
      (agents ?? [])
        .toSorted((a, b) => a.order - b.order)
        .map(({ key, label }) => ({ value: key, label })),
    [agents],
  );

  // Track only the explicit selection; agentKey is derived below.
  const [explicitAgentKey, setExplicitAgentKey] = useState<string>();
  const agentKey = resolveAgentKey(
    explicitAgentKey,
    defaultAgent,
    agentOptions,
  );

  const [input, setInput] = useState("");

  const { messages, isStreaming, sendMessage, reset } =
    useAgentStream(agentKey);

  const handleAgentChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      setExplicitAgentKey(event.target.value);
      reset();
    },
    [reset],
  );

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || isStreaming) {
      return;
    }
    setInput("");
    sendMessage(trimmed, context);
  }, [context, input, isStreaming, sendMessage]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // Context label for display; empty string when no context is active.
  const contextLabel = useMemo(
    () =>
      [
        context.projectId && `Project: ${context.projectId}`,
        context.predictorId && `Predictor: ${context.predictorId}`,
        context.designSpaceId && `Design Space: ${context.designSpaceId}`,
        context.workspaceId && `Workspace: ${context.workspaceId}`,
        optimizationRunId && `Optimization Run: ${optimizationRunId}`,
      ]
        .filter(Boolean)
        .join(" · "),
    [context, optimizationRunId],
  );

  return (
    <catalystSx.Panel data-testid={TEST_IDS.drawerContent}>
      <CatalystHeader
        disabled={isStreaming}
        subheader={
          <Select
            value={agentKey}
            options={agentOptions}
            onChange={handleAgentChange}
            label={formatMessage({ id: "catalyst.agent" })}
            size="small"
            fullWidth
            disabled={isStreaming || agentOptions.length === 0}
            data-testid="catalyst-agent-selector"
          />
        }
      />

      {contextLabel && enableCatalystAgentDebug ? (
        <sx.ContextIndicator title={contextLabel}>
          <TruncatedText>
            <sx.ContextIndicatorText component="span">
              {contextLabel}
            </sx.ContextIndicatorText>
          </TruncatedText>
        </sx.ContextIndicator>
      ) : null}

      {/* MessageList is the scroller itself (flush to the panel's right edge),
          rather than nesting inside the padded Content wrapper. */}
      <AgentChatMessageList isStreaming={isStreaming} messages={messages} />

      <sx.InputArea>
        <TextField
          autoFocus={expanded}
          fullWidth
          // Remount on expand so `autoFocus` re-fires. The input stays mounted across
          // collapse/expand, so autoFocus alone won't refocus it. We remount instead of
          // using `inputRef` because, with an `inputRef` set, this TextField trips a MUI
          // Tooltip ref-forwarding warning whenever it mounts/unmounts.
          key={String(expanded)}
          multiline
          maxRows={4}
          size="small"
          placeholder={formatMessage({
            id: "catalyst.agent.question-placeholder",
          })}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isStreaming}
          slotProps={{ htmlInput: { "data-testid": "catalyst-agent-input" } }}
        />
        <IconButton
          aria-label={formatMessage({ id: "Send" })}
          color="primary"
          onClick={handleSend}
          disabled={!input.trim() || isStreaming}
          data-testid="catalyst-agent-send"
        >
          <SendIcon />
        </IconButton>
      </sx.InputArea>
    </catalystSx.Panel>
  );
}
